import { useNavigate } from 'react-router-dom';
import { useStockTrace } from '../api/hooks';
import type { StockStatus, UnitTrace } from '../api/types';
import { money, plural } from '../lib/format';
import { H2, Surface, Tag } from './ui';

const STATUS: Record<StockStatus, string> = {
  in_stock: 'skladem',
  reserved: 'rezervováno',
  sold: 'prodáno',
  returned: 'vráceno dodavateli',
  written_off: 'odepsáno',
  in_service: 'v servisu',
};

/**
 * Párování nákup ↔ prodej v detailu dokladu: u přijaté faktury kusy, které naskladnila
 * (regál, stav, komu se prodaly a s jakou marží), u prodeje odkud byl každý kus nakoupený.
 */
export function StockTraceSection({
  documentId,
  currency = 'CZK',
}: {
  documentId: string;
  currency?: string;
}) {
  const navigate = useNavigate();
  const { data } = useStockTrace(documentId);
  if (!data) return null;
  const purchased = data.items.flatMap((i) => i.purchased);
  const sold = data.items.flatMap((i) => i.sold);
  const receipts = data.items.flatMap((i) => i.movements.filter((m) => m.type === 'receipt'));
  if (!purchased.length && !sold.length && !receipts.length) return null;
  const fmt = (v: string) => money(v, currency);
  const variant = (u: UnitTrace) => (u.variant ? ` · ${u.variant}` : '');

  return (
    <>
      {purchased.length > 0 && (
        <>
          <H2>
            Naskladněné zboží <span className="cnt">{purchased.length}</span>
          </H2>
          <p className="muted trace-sum">
            skladem {data.summary.inStock} · prodáno {data.summary.sold}{' '}
            {plural(data.summary.sold, ['kus', 'kusy', 'kusů'])}
            {data.summary.sold > 0 ? ` · marže ${fmt(data.summary.margin)}` : ''}
          </p>
          <Surface>
            {purchased.map((u) => (
              <div className="row" key={u.id}>
                <div className="grow">
                  <div className="l1">
                    {u.product}
                    {variant(u)}
                  </div>
                  <div className="l2">
                    <span className="mono">{u.serial}</span>
                    {u.grade && <span>stav {u.grade}</span>}
                    {u.location && <span>regál {u.location}</span>}
                    <span>nákup {fmt(u.purchasePrice)}</span>
                  </div>
                </div>
                {u.sale ? (
                  <button
                    type="button"
                    className="link trace-link"
                    onClick={() => navigate(`/doklady/${u.sale?.documentId}`)}
                  >
                    prodáno {u.sale.number ?? ''} · {u.sale.customerName}
                    {u.margin ? ` · marže ${fmt(u.margin)}` : ''}
                  </button>
                ) : (
                  <Tag ok={u.status === 'in_stock'}>{STATUS[u.status]}</Tag>
                )}
              </div>
            ))}
          </Surface>
        </>
      )}
      {receipts.length > 0 && (
        <>
          <H2>Naskladněno na počet</H2>
          <Surface>
            {receipts.map((m) => (
              <div className="row" key={m.id}>
                <div className="grow">
                  <div className="l1">{m.product}</div>
                  <div className="l2">
                    {m.location && <span>regál {m.location}</span>}
                    {m.unitCost && (
                      <span>
                        nákup {fmt(m.unitCost)} / {m.unit}
                      </span>
                    )}
                  </div>
                </div>
                <div className="amt">
                  {m.quantity.replace(/\.0+$/, '')} {m.unit}
                </div>
              </div>
            ))}
          </Surface>
        </>
      )}
      {sold.length > 0 && (
        <>
          <H2>Původ prodaného zboží</H2>
          <Surface>
            {sold.map((u) => (
              <div className="row" key={u.id}>
                <div className="grow">
                  <div className="l1">
                    {u.product}
                    {variant(u)}
                  </div>
                  <div className="l2">
                    <span className="mono">{u.serial}</span>
                    {u.grade && <span>stav {u.grade}</span>}
                    {u.purchase && (
                      <span>
                        od {u.purchase.supplierName}
                        {u.purchase.externalNumber ? ` (${u.purchase.externalNumber})` : ''}
                      </span>
                    )}
                    <span>nákup {fmt(u.purchasePrice)}</span>
                    {u.margin && <span>marže {fmt(u.margin)}</span>}
                  </div>
                </div>
                {u.purchase?.documentId && (
                  <button
                    type="button"
                    className="link trace-link"
                    onClick={() => navigate(`/doklady/${u.purchase?.documentId}`)}
                  >
                    nákupní faktura {u.purchase.number}
                  </button>
                )}
              </div>
            ))}
          </Surface>
        </>
      )}
    </>
  );
}
